import * as _ from 'lodash';
import moment, { Moment } from 'moment-timezone';
import { SelectionType } from 'app/shared/selection/SelectionType';
import { Selection } from 'app/shared/selection/Selection';
import { Cartesian3Util } from '../shared/utilities/cartesian3_util.service';

const URL_PARAM_DEFAULTS: SavedParameters = {
    live: true,
    endTime: null,
    selectedTime: null,
    siteId: undefined,
    view: null,
    cameraPos: null,
    cameraDir: null,
    cameraUp: null,
    selection: [],
    '3D': false,
};

interface SavedParameters {
    cameraDir?: Array<{ value: number }>;
    cameraPos?: Array<{ value: number }>;
    cameraUp?: Array<{ value: number }>;
    endTime?: dateAndTimeObject;
    live?: boolean;
    '3D'?: boolean;
    selectedTime?: dateAndTimeObject;
    siteId?: number;
    view?: string;
    selection?: Array<RawSelections>;
    [key: string]: any; //there are a bunch of other parameters that we don't care about
}

interface RawSelections {
    value: string;
}

interface ParsedCameraParams {
    cameraPosition: Cesium.Cartesian3;
    cameraDirection: Cesium.Cartesian3;
    cameraUp: Cesium.Cartesian3;
}

interface dateAndTimeObject {
    value: string;
    extra: string;
}

function splitUpSelections(selection: string): { id: number; type: SelectionType } {
    const deconstructedValue = selection.split('-');
    return { id: parseInt(deconstructedValue[1]), type: deconstructedValue[0] as SelectionType };
}

function parseSelections(selections: Array<RawSelections>): Array<Selection> {
    return selections.map((selection) => {
        const { id, type } = splitUpSelections(selection.value);
        return new Selection(id, type);
    });
}

function parseDateTime(dateAndTimeObject: dateAndTimeObject): Moment {
    if (dateAndTimeObject) {
        let constructedDateTime = dateAndTimeObject.value + ':' + dateAndTimeObject.extra;
        return moment(new Date(constructedDateTime));
    } else {
        return null;
    }
}

function parseCameraParams(
    cameraPos: Array<{ value: number }>,
    cameraDir: Array<{ value: number }>,
    cameraUp: Array<{ value: number }>,
    cartesian3Util: Cartesian3Util,
): ParsedCameraParams {
    let parsedCameraPos = cameraPos ? cartesian3Util.parseObjectArray(cameraPos) : null;
    let parsedCameraDir = cameraDir ? cartesian3Util.parseObjectArray(cameraDir) : null;
    let parsedCameraUp = cameraUp ? cartesian3Util.parseObjectArray(cameraUp) : null;
    return {
        cameraPosition: parsedCameraPos,
        cameraDirection: parsedCameraDir,
        cameraUp: parsedCameraUp,
    };
}

export class UrlInitParams {
    private _params: SavedParameters;
    private _cameraPosition: Cesium.Cartesian3;
    private _cameraDirection: Cesium.Cartesian3;
    private _cameraUp: Cesium.Cartesian3;
    private _live: boolean;
    private _selectedTime: Moment;
    private _timelineEnd: Moment;
    private _initialSelections: Array<Selection>;
    private _mapTerrainEnabled: boolean;

    constructor(params: SavedParameters = {} as SavedParameters, cartesian3Util: Cartesian3Util) {
        this._params = _.merge({}, URL_PARAM_DEFAULTS, params);

        ({
            cameraPosition: this._cameraPosition,
            cameraDirection: this._cameraDirection,
            cameraUp: this._cameraUp,
        } = parseCameraParams(
            this._params.cameraPos,
            this._params.cameraDir,
            this._params.cameraUp,
            cartesian3Util,
        ));

        this._initialSelections = parseSelections(this._params.selection);

        if (this._params.live) {
            this._live = true;
            this._selectedTime = null;
            this._timelineEnd = null;
        } else {
            this._live = false;
            this._selectedTime = parseDateTime(this._params.selectedTime);
            this._timelineEnd = parseDateTime(this._params.endTime);
        }

        this._mapTerrainEnabled = this._params['3D'];
    }

    /**
     * An array of selections that should be initally selected
     * @returns {Selection[]}
     */
    get initialSelections(): Selection[] {
        return this._initialSelections;
    }

    /**
     * The momentjs object of where our time cursor should be
     * @returns {moment.Moment|*|null}
     */
    get selectedTimeMoment(): Moment {
        return this._selectedTime;
    }

    /**
     * The momentjs object of where our timeline should end
     */
    get timelineEndMoment(): Moment {
        return this._timelineEnd;
    }

    /**
     * Is the app in live mode?
     * @returns boolean
     */
    get isLive(): boolean {
        return this._live;
    }

    /**
     * Gets the site id that should be selected
     * Can return undefined which means that no site id was specified.
     * Can return null which means that the default site was requested.
     * @returns *
     */
    get siteId(): number {
        return this._params.siteId;
    }

    /**
     * Gets the view type name that should be selected
     *
     * @returns string
     */
    get view(): string {
        return this._params.view;
    }

    /**
     * Get the camera position
     * @returns {*|Cesium.Cartesian3|{fromElements}}
     */
    get cameraPosition(): Cesium.Cartesian3 {
        return this._cameraPosition;
    }

    /**
     * Get the camera direction
     * @returns {*|Cesium.Cartesian3|{fromElements}}
     */
    get cameraDirection(): Cesium.Cartesian3 {
        return this._cameraDirection;
    }

    /**
     * Get the camera up vector
     * @returns {*|Cesium.Cartesian3|{fromElements}}
     */
    get cameraUp(): Cesium.Cartesian3 {
        return this._cameraUp;
    }

    /**
     * Get mapTerrainEnabled
     */

    get mapTerrainEnabled(): boolean {
        return this._mapTerrainEnabled;
    }

    /**
     * Cesium needs 3 vectors to correctly move the camera. Position,
     * direction and up. This method will let the caller know whether
     * this url init params has all the necessary information to position
     * the camera
     * @returns {boolean}
     */
    get hasValidCameraPosition(): boolean {
        return (
            !_.isEmpty(this._cameraPosition) &&
            !_.isEmpty(this._cameraDirection) &&
            !_.isEmpty(this._cameraUp)
        );
    }
}
